import React, { Fragment, useState, useMemo, useEffect } from 'react';

import { getIndentClass } from '../../utils/styleUtils';

import ExpenseCostcenterRow from './ExpenseCostcenterRow';
import ExpenseProjectRow from './ExpenseProjectRow';
import ExpenseRow from './ExpenseRow';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';

function ExpenseDepartmentRow(props) {
    const [showDepartment, setShowDepartment] = useState(false);

    // Handle expand all trigger from parent with staggered updates
    useEffect(() => {
        const t = props.expandAllTrigger;
        if (!t?.version) return;

        if (t.batch === 'departments') {
            setShowDepartment(t.expanded);
        }
    }, [props.expandAllTrigger?.version]);

    // Calculate indentation levels
    const childIndentLevel = useMemo(() => props.indentLevel + 1, [props.indentLevel]);

    // Memoize common props to prevent unnecessary object creation
    const commonProps = useMemo(
        () => ({
            ExpenseMilestoneUpdateUrl: props.ExpenseMilestoneUpdateUrl,
            minDate: props.minDate,
            maxDate: props.maxDate,
            maxExpenseMilestonesCount: props.maxExpenseMilestonesCount,
            increaseMaxExpenseMilestonesCount: props.increaseMaxExpenseMilestonesCount,
            editingExpenseId: props.editingExpenseId,
            editingType: props.editingType,
            editingMilestoneId: props.editingMilestoneId,
            setExpenseEditing: props.setExpenseEditing,
            modalShow: props.modalShow,
            canEdit: props.canEdit,
            costcentersColor: props.costcentersColor,
            milestoneType: props.milestoneType,
            periods: props.periods,
            setLoading: props.setLoading,
            fetchCentralCashflow: props.fetchCentralCashflow,
            indentLevel: childIndentLevel,
            expandAllTrigger: props.expandAllTrigger,
            editAllTrigger: props.editAllTrigger,
        }),
        [props, childIndentLevel],
    );

    // Memoize costcenter rows
    const costcenterRows = useMemo(() => {
        const costcenters = Object.values(props.department.costcenters || {});
        if (!costcenters || costcenters.length === 0) {
            return null;
        }

        return costcenters.map((costcenter) => <ExpenseCostcenterRow {...commonProps} key={`tr_costcenter_${costcenter['id']}`} costcenter={costcenter} />);
    }, [props.department.costcenters, commonProps]);

    // Memoize project rows
    const projectRows = useMemo(() => {
        if (!props.department.projects || props.department.projects.length === 0) return null;

        return props.department.projects.map((project) => <ExpenseProjectRow {...commonProps} key={`tr_expense_pr_${project.id}`} project={project} />);
    }, [props.department.projects, commonProps]);

    // Memoize expense rows
    const expenseRows = useMemo(() => {
        if (!props.department.expenses || props.department.expenses.length === 0) return null;

        return props.department.expenses.map((expense) => <ExpenseRow {...commonProps} key={`tr_expense_${expense.id}`} expense={expense} />);
    }, [props.department.expenses, commonProps]);

    return (
        <>
            <tr className="!tw-bg-blue-25">
                <td className="tw-font-bold tw-cursor-pointer !tw-border-r-0" onClick={() => setShowDepartment(!showDepartment)}>
                    <div className={`tw-flex tw-items-center tw-gap-x-1.5 ${getIndentClass(props.indentLevel)}`}>
                        <div className="!tw-text-blue">{props.department.name}</div>
                        <div>{showDepartment ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>
                    </div>
                </td>
                <td className="tw-p-4 !tw-border-x-0"></td>
                <td className="tw-font-bold tw-cursor-pointer tw-border-r-2" onClick={() => setShowDepartment(!showDepartment)}></td>
                <td
                    colSpan={props.maxExpenseMilestonesCount * 2 + 3}
                    className="!tw-bg-blue-25 tw-font-bold tw-cursor-pointer !tw-border-l-0"
                    onClick={() => setShowDepartment(!showDepartment)}
                ></td>
            </tr>

            {showDepartment && (
                <Fragment>
                    {costcenterRows}
                    {projectRows}
                    {expenseRows}
                </Fragment>
            )}
        </>
    );
}

// Memoize component to prevent unnecessary re-renders
export default React.memo(ExpenseDepartmentRow);
